import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Manufacturers (bugs: New Item — IT-S6 "Manufacturer"; Stock levels — ST-02
 * "more filters: manufacturer") — who makes an item.
 *
 * The specification has no MD screen and no rule for this; the owner's note
 * asks for the field and the filter. So this is the smallest honest master:
 * a tenant-owned name list with the optional Arabic twin (S5), on/off like
 * every vocabulary (off = not pickable on a NEW item; items already naming it
 * keep their maker), and no delete — a maker that was ever named is history
 * on the items that name it.
 *
 * Permission area: `inventory` — the items module's own (PART 16.2.2 row 3).
 *
 * THE SENTENCES ARE FIXED ON PURPOSE. None carries a value, so each maps to
 * one dictionary key by exact match (`lib/server-messages.ts`) and reaches an
 * Arabic reader in Arabic without a structured-refusal family of its own.
 */
export const MANUFACTURER_MESSAGES = {
    nameRequired: 'Type the manufacturer’s name.',
    nameTaken: 'A manufacturer with this name already exists.',
    /** MD-RULE-2's shape: off = not pickable on a NEW item. */
    inactive: 'This manufacturer is switched off and cannot be picked on a new item.',
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ManufacturerSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    /** S5: the optional Arabic twin; `localizedName()` picks it on an Arabic screen. */
    nameAr: z.string().nullable(),
    isActive: z.boolean(),
    /** How many items name this maker — the list column, and why there is no delete. */
    itemCount: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreateManufacturerRequestSchema = z
    .object({
    name: z.string().trim().min(1, MANUFACTURER_MESSAGES.nameRequired).max(120),
    nameAr: z.string().trim().min(1).max(120).nullable().optional(),
})
    .strict();
export const UpdateManufacturerRequestSchema = z
    .object({
    name: z.string().trim().min(1, MANUFACTURER_MESSAGES.nameRequired).max(120).optional(),
    nameAr: z.string().trim().min(1).max(120).nullable().optional(),
})
    .strict();
const manufacturerParams = z.object({ manufacturerId: z.uuid() }).strict();
export const manufacturersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/manufacturers',
        query: z
            .object({
            /** The maintenance screen's switch; a picker asks without it. */
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ manufacturers: ManufacturerSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The makers list (inventory.view) — a vocabulary, unpaged like every other; name order.',
    },
    create: {
        method: 'POST',
        path: '/api/manufacturers',
        body: CreateManufacturerRequestSchema,
        responses: {
            201: ManufacturerSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'A new maker (inventory.create); a duplicate name is refused, case-insensitively.',
    },
    update: {
        method: 'PATCH',
        path: '/api/manufacturers/:manufacturerId',
        pathParams: manufacturerParams,
        body: UpdateManufacturerRequestSchema,
        responses: {
            200: ManufacturerSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Rename (inventory.edit) — reaches every item at once; the reference is the id.',
    },
    deactivate: {
        method: 'POST',
        path: '/api/manufacturers/:manufacturerId/deactivate',
        pathParams: manufacturerParams,
        body: z.object({}).strict().optional(),
        responses: { 200: ManufacturerSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch off (inventory.edit): not pickable on a new item; items already naming it keep it.',
    },
    reactivate: {
        method: 'POST',
        path: '/api/manufacturers/:manufacturerId/reactivate',
        pathParams: manufacturerParams,
        body: z.object({}).strict().optional(),
        responses: { 200: ManufacturerSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch back on (inventory.edit).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=manufacturers.js.map